import {DrupalNode, DrupalTaxonomyTerm} from "next-drupal";
import {useState, Fragment, RefObject, useContext} from "react";
import {
  Dictionary,
  DrupalProductNode,
  DrupalSearchApiResponseMeta,
  IndustrySegmentsTaxonomyTerm,
  ProductTypeTaxonomyTerm
} from "../../../../types/hygiena-types";
import classNames from "classnames";
import {getParagraphSections} from "../../../../lib/get-paragraph-sections";

import dynamic from "next/dynamic";
import {IndustrySegmentProductGrid} from "../../../molecules/industry-segment-product-grid";
import {DictionaryContext} from "../../../../context/dictionary-context";
import {TrainingVideosSlider} from "../../../organisms/slider";
import {ModalContext} from "../../../../context/modal-context";

const RenderSections = dynamic(() => import("../../sections/section-regions").then((mod) => mod.RenderSections));
const MissingProduct = dynamic(() => import("../../../molecules/missing-product").then((mod) => mod.MissingProduct));
const Heading = dynamic(() => import("../../../atoms/heading").then((mod) => mod.Heading));
const Body = dynamic(() => import("../../../atoms/body").then((mod) => mod.Body));
const DynamicProductGrid = dynamic(() => import("../../../organisms/widget--product-grid").then((mod) => mod.DynamicProductGrid));
const SectionHeading = dynamic(() => import("../../../organisms/section-heading"));
const ArticleSlider = dynamic(() => import("../../../organisms/slider").then((mod) => mod.ArticleSlider));
const ImageIcon = dynamic(() => import("../../../atoms/icon").then((mod) => mod.ImageIcon));
const TaxonomyIndustrySegmentsTeaser = dynamic(() => import("../taxonomy--industry_segments--teaser").then((mod) => mod.TaxonomyIndustrySegmentsTeaser));
const FormattedText = dynamic(() => import("../../../formatted-text").then((mod) => mod.FormattedText));

/**
 * The product selection grid.
 *
 * @param products
 * @constructor
 */
function IndustrySegmentsCategoryProductSection({
  term,
  products,
  gridKey,
}: {
  term: {name: string, id: string, weight: number},
  products?: DrupalProductNode[],
  gridKey?: string,
}) {
  const [active, setActive] = useState(false);
  const t = useContext<Dictionary>(DictionaryContext)

  /**
   * Create a simple triangle.
   *
   * @todo this is also used in key_value_page--support.tsx, let's refactor.
   *
   * @constructor
   */
  function Triangle({active}: {active: boolean}): JSX.Element {
    return <span className={classNames(
      "flex pr-4 transition-all duration-500 ease-in-out",
      {"transform rotate-90 -translate-x-5 translate-y-2.5": active},
    )}>
      <ImageIcon type={"chevron-right"} color={"primary"} height={25} width={25} alt={"Next"}/>
    </span>
  }

  return (
    // @todo this is re-used code from the key_value_page--support.tsx FAQ section - refactor.
    <div key={term.id}>
      <div className="flex bg-white drop-shadow-sm cursor-pointer py-5" onClick={() => setActive(!active)}>
        <h3 className="text-black px-8 text-left md:!text-3xl font-light flex-grow">
          {term.name}
        </h3>
        <Triangle active={active}/>
      </div>
      <div className={classNames(
        "overflow-hidden transition-all delay-200 ease-linear origin-top py-4",
        {"hidden": !active},
      )}>
        <DynamicProductGrid key={gridKey ?? undefined} nodes={products}/>
        <MissingProduct/>
      </div>
    </div>
  )
}

/**
 * @todo this is the same as WorkflowButton, refactor it!
 *
 * @param icon
 * @param callback
 * @param active
 * @constructor
 */
function ProductionStepButton ({icon, callback, active, title}) {
  const [hover, setHover] = useState(false);

  return (
    <div className="inline-block mx-auto mb-4 min-h-[100px] min-w-[100px]">
      <button onClick={callback}
              aria-label={title}
              role="button"
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              data-step={`${title}`}
              className={classNames(
                "btn-industry-step cursor-pointer hover:bg-primary border-[3px] flex justify-center items-center border-primary rounded-md transition-all duration-500",
                {"bg-primary": active},
              )}>
        <div className="min-h-[100px] min-w-[100px] flex items-center justify-center cursor-pointer relative">
          <ImageIcon icon={icon} color={"white"} height={50} width={50} alt={title} className={classNames("inline m-2 absolute", {"opacity-0": !active && !hover})} />
          <ImageIcon icon={icon} color={"primary"} height={50} width={50} alt={title} className={classNames("inline m-2 absolute", {"opacity-0": active || hover})}/>
        </div>
      </button>
    </div>
  );
}

/**
 * The Industry/Segment page.
 *
 * @param taxonomy_term
 * @param additionalContent
 * @param solutionsRef
 * @constructor
 */
export function ContentIndustrySegments ({
  taxonomy_term,
  additionalContent,
  solutionsRef,
}: {
  taxonomy_term: IndustrySegmentsTaxonomyTerm,
  solutionsRef: RefObject<any>,
  additionalContent?: {
    relatedNews?: DrupalNode[],
    products?: DrupalProductNode[],
    childTerms?: IndustrySegmentsTaxonomyTerm[],
  }
}): JSX.Element {
  const t = useContext<Dictionary>(DictionaryContext);
  const childTerms = additionalContent?.childTerms?.filter(item => item?.path?.alias);
  const {sections, sectionMap} = getParagraphSections(taxonomy_term?.field_industry_layout_components);
  const products: DrupalProductNode[] = additionalContent?.["products"] ?? [];
  const meta: DrupalSearchApiResponseMeta = additionalContent?.["productMeta"] ?? {};
  const childTermsCount = childTerms?.length;
  const steps: DrupalTaxonomyTerm[] = additionalContent?.["steps"] ?? [];
  const {setOpenModal, setOpenSmallModal, setOpenModalContent, setOpenSmallModalContent} = useContext(ModalContext);

  return (
    <>
      <article className="px-6 m-auto md:px-0">
        {taxonomy_term?.description?.processed && (
          <div className="container pt-12 md:pt-24 mx-auto text-xl text-black">
            <Body bodyType="lg">
              <FormattedText processed={taxonomy_term.description.value}/>
            </Body>
          </div>
        )}
        {/* Layout Components */}
        {sections?.length ? <RenderSections sections={sections} sectionMap={sectionMap}/> : <></>}
        {childTermsCount ? (
          <>
            <div className="container py-12 md:py-24 mx-auto scroll-m-20" ref={solutionsRef}>
              {(taxonomy_term?.field_industry_segment_header || taxonomy_term?.field_industry_segment_text) && (
                <SectionHeading title={taxonomy_term?.field_industry_segment_header ?? ""}>
                  {taxonomy_term?.field_industry_segment_text?.processed && <FormattedText processed={taxonomy_term.field_industry_segment_text.processed}/>}
                </SectionHeading>
              )}
              {taxonomy_term?.field_product_type_child_title || taxonomy_term?.field_product_type_child_desc ? (
                <div className="mb-6">
                  {taxonomy_term?.field_product_type_child_title && (
                    <div className="mb-4">
                      <h2 className="mb-2 text-black inline">
                        {taxonomy_term?.field_product_type_child_title}
                      </h2>
                    </div>
                  )}
                  {taxonomy_term?.field_product_type_child_desc?.processed && (
                    <Body bodyType="lg" className="mb-12 leading-relaxed font-light">
                      <FormattedText processed={taxonomy_term.field_product_type_child_desc.processed}/>
                    </Body>
                  )}
                </div>
              ): <></>}
              <div className={classNames(
                "grid grid-cols-2 gap-1",
                {"md:grid-cols-3": childTermsCount <= 9},
                {"md:grid-cols-4": childTermsCount > 9 && childTermsCount < 20},
                {"md:grid-cols-5": childTermsCount >= 20}
              )}>
                {childTerms.map(item => {
                  const child = item?.field_industry_segment_link_to?.id ? item.field_industry_segment_link_to : item;
                  return (
                    <TaxonomyIndustrySegmentsTeaser taxonomy_term={child as ProductTypeTaxonomyTerm}
                                                    className={classNames(
                                                      "max-md:min-h-[120px]",
                                                      {"md:h-64": childTermsCount <= 9},
                                                      {"md:h-48": childTermsCount > 9 && childTermsCount < 20},
                                                      {"md:h-40": childTermsCount >= 20}
                                                    )}
                                                    key={child.id} />
                  );
                })}
              </div>
            </div>
          </>
        ): <></>}
        {products?.length ? (
          <IndustrySegmentProductGrid products={products}
                                      term={taxonomy_term}
                                      useQueryParams={true}
                                      scrollRef={(!childTerms?.length && products) ? solutionsRef : undefined} steps={steps}
                                      gridKey={`industry-segment-product-grid-${taxonomy_term.id}`} meta={meta} />
        ): <></>}
        {taxonomy_term?.field_industry_videos?.length ? (
          <div className="container pt-6 mx-auto">
            <div className={"mb-6"}>
              <SectionHeading title={`${t?.all?.product_videos}`}/>
            </div>
            <TrainingVideosSlider videos={taxonomy_term.field_industry_videos ?? []} usePath={true} openModalContent={setOpenModalContent} openModal={setOpenModal}/>
          </div>
        ): <></>}
        {additionalContent?.relatedNews?.length ? (
          <div className="container pb-12 md:py-24 mx-auto">
            <div className={"mb-6"}>
              <SectionHeading title={`${t?.all?.related_news}`} link={{uri: "/news", url: "/news", title: `${t?.all?.see_all_news}`}}/>
            </div>
            <ArticleSlider articles={additionalContent?.relatedNews}/>
          </div>
        ): <></>}
      </article>
    </>
  )
}
